import { Select } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DownIcon } from '@/components/Icons';

import { Sort, SortDir } from '@/models/props/ProductCardProps';
import { SortingProductsProps } from '@/models/props/SortingProductsProps';

export type SortOptionValue = {
    sortDir?: SortDir;
    sort?: Sort;
};

export type DropdownOptionItem = {
    label: string;
} & SortOptionValue;

export type SortParams = SortOptionValue;

export const SortingProducts = (props: SortingProductsProps) => {
    const router = useRouter();
    const session = useSession();
    const { selectValue } = props;
    const selectRef = useRef<HTMLSelectElement>(null);
    const [selectedOption, setSelectedOption] = useState<string>('');

    const dropdownOptions = useMemo<DropdownOptionItem[]>(() => {
        const options: DropdownOptionItem[] = [];
        options.push({ label: 'sort.default', sortDir: 'ASC' });

        if (session.data) {
            options.push(
                { label: 'sort.points-asc', sortDir: 'ASC', sort: 'POINTS' },
                { label: 'sort.points-desc', sortDir: 'DESC', sort: 'POINTS' }
            );
        }

        options.push(
            { label: 'sort.price-asc', sortDir: 'ASC', sort: 'PRICE' },
            { label: 'sort.price-desc', sortDir: 'DESC', sort: 'PRICE' },
            { label: 'sort.name-asc', sortDir: 'ASC', sort: 'NAME' },
            { label: 'sort.name-desc', sortDir: 'DESC', sort: 'NAME' }
        );

        return options;
    }, [session.data]);

    const sortingHandle = () => {
        const sorting = selectRef.current?.value as string;
        const sortObject = JSON.parse(sorting) as SortOptionValue;
        updateURL(sortObject);
        selectValue(sortObject);
    };

    const serialize = useCallback((data: SortOptionValue): string => {
        return JSON.stringify(data);
    }, []);

    const updateURL = useCallback(
        async (sortData: SortOptionValue) => {
            const url = new URL(location.href);
            const { sort, sortDir } = sortData;
            if (!sortDir) {
                url.searchParams.delete('sortDir');
            } else {
                url.searchParams.set('sortDir', sortDir);
            }

            if (!sort) {
                url.searchParams.delete('sort');
            } else {
                url.searchParams.set('sort', sort);
            }
            await router.push({
                query: {
                    ...router.query,
                    sort,
                    sortDir
                }
            });
        },
        [router]
    );

    useEffect(() => {
        const sort = props.sort || router.query.sort;
        const sortDir = props.sortDir || router.query.sortDir;
        const matchedOption: SortOptionValue =
            dropdownOptions.find((item) => item.sort === sort && item.sortDir === sortDir) || {};
        setSelectedOption(() =>
            serialize({
                sort: matchedOption.sort,
                sortDir: matchedOption.sortDir
            })
        );
    }, [props, setSelectedOption, serialize, dropdownOptions, router.query]);

    return (
        <Select
            id="sorting"
            mr={{ base: 0, md: 8 }}
            borderRadius="full"
            borderColor="blue.200"
            iconSize="10px"
            icon={<DownIcon color="grey.200" />}
            width={{ base: '100%', md: 260 }}
            ref={selectRef}
            onChange={sortingHandle}
            value={selectedOption}
        >
            {dropdownOptions.map((item, index) => {
                const { sort, sortDir } = item;
                const option: SortOptionValue = { sort, sortDir };
                return (
                    <option key={index} value={serialize(option)}>
                        <FormattedMessage id={item.label}></FormattedMessage>
                    </option>
                );
            })}
        </Select>
    );
};
